import React, { useState } from 'react';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Check, KeyRound, AlertTriangle } from 'lucide-react';
import { apiKeys } from '../api';
import type { ApiKeyMeta, ApiKeyCreated } from '../api';
import { format } from 'date-fns';

const ROLES = ['ADMIN', 'OPERATIONS', 'PRODUCT_MANAGER', 'SUPPORT', 'BILLING'];

function CreateKeyModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [role, setRole] = useState('OPERATIONS');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: () => apiKeys.create(name, role),
    onSuccess: (result) => {
      setCreated(result);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Erstellen fehlgeschlagen.';
      setError(msg);
    },
  });

  function handleClose() {
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-5 border-b border-slate-200">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <KeyRound size={16} /> Neuer API-Key
          </h2>
        </div>

        {!created ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              createMutation.mutate();
            }}
            className="px-6 py-5 space-y-4"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.nameRequired')}</label>
              <input
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('apiKeys.nameExample')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rolle</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Bestimmt, welche Endpunkte der Key nutzen darf — genau wie bei einem Nutzer-Login.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg disabled:opacity-60"
              >
                {createMutation.isPending ? 'Erstelle…' : 'Erstellen'}
              </button>
            </div>
          </form>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 rounded-lg text-xs flex gap-2">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>Dieser Key wird nur jetzt angezeigt — danach nicht mehr abrufbar. Jetzt kopieren.</span>
            </div>
            <div className="flex gap-2">
              <code className="flex-1 bg-konsole text-konsole-gruen px-3 py-2 rounded-lg text-xs font-mono break-all">
                {created.rawKey}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(created.rawKey);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="shrink-0 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-surface rounded-lg text-xs flex items-center gap-1.5"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Kopiert' : 'Kopieren'}
              </button>
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <p>{t('apiKeys.cliHintBefore')} <code className="bg-slate-100 px-1 rounded">JUNI_PC_API_KEY</code> {t('apiKeys.cliHintAfter')}</p>
              <code className="block bg-slate-100 px-2 py-1.5 rounded">Authorization: Bearer {created.rawKey.slice(0, 14)}…</code>
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={handleClose} className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg">
                Fertig
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KeyRow({ apiKey }: { apiKey: ApiKeyMeta }) {
  const qc = useQueryClient();
  const revokeMutation = useMutation({
    mutationFn: () => apiKeys.revoke(apiKey.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const revoked = !!apiKey.revokedAt;

  return (
    <tr className={revoked ? 'opacity-50' : ''}>
      <td className="px-4 py-3 font-medium text-slate-800">{apiKey.name}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">{apiKey.keyPrefix}…</td>
      <td className="px-4 py-3 text-slate-600">{apiKey.role}</td>
      <td className="px-4 py-3 text-slate-500 text-xs">
        {apiKey.lastUsedAt ? format(new Date(apiKey.lastUsedAt), 'PPp') : 'nie verwendet'}
      </td>
      <td className="px-4 py-3 text-slate-500 text-xs">{format(new Date(apiKey.createdAt), 'PPp')}</td>
      <td className="px-4 py-3">
        {revoked ? (
          <span className="text-xs text-red-500 font-medium">Widerrufen</span>
        ) : (
          <button
            onClick={() => {
              if (confirm(`API-Key "${apiKey.name}" widerrufen?`)) revokeMutation.mutate();
            }}
            disabled={revokeMutation.isPending}
            className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-40"
          >
            Widerrufen
          </button>
        )}
      </td>
    </tr>
  );
}

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['api-keys'], queryFn: apiKeys.list });

  const rows = data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'name', label: t('common.name'), wert: (k) => k.name },
    { key: 'praefix', label: t('tables.prefix'), wert: (k) => k.keyPrefix ?? '' },
    { key: 'rolle', label: t('common.role'), wert: (k) => k.role },
    { key: 'zuletzt', label: t('tables.lastUsed'), wert: (k) => k.lastUsedAt },
    { key: 'erstellt', label: t('common.createdAt'), wert: (k) => k.createdAt },
  ];

  const tabelle = useTabelle({
    speichern: 'api-schluessel',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'erstellt', richtung: 'ab' },
  });


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Für CLI-, MCP- und Agent-Zugriff — kein Login/JWT-Ablauf, individuell widerrufbar.
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
        >
          <Plus size={15} /> Key erstellen
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400 italic">
                  Noch keine API-Keys. Klicke auf „Key erstellen".
                </td>
              </tr>
            ) : (
              tabelle.sichtbar.map((k) => <KeyRow key={k.id} apiKey={k} />)
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {showCreate && <CreateKeyModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
